import { useEffect, useState, useMemo, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { api } from "@/lib/api";
import { Lock, Search, X, ArrowRight, Download, BookOpen, Layers, Mail, CalendarDays, ChevronDown } from "lucide-react";

const TYPES = [
  { key: "all", label: "All" },
  { key: "reading", label: "Read With Me" },
  { key: "study", label: "Studies" },
  { key: "series", label: "Series" },
  { key: "newsletter", label: "Newsletters" },
];

const SORTS = [
  { key: "newest", label: "Newest" },
  { key: "oldest", label: "Oldest" },
  { key: "az", label: "A–Z" },
  { key: "za", label: "Z–A" },
];

const TYPE_META = {
  reading:    { color: "var(--ember)", label: "Read With Me", Icon: CalendarDays },
  study:      { color: "var(--olive)", label: "Study", Icon: BookOpen },
  series:     { color: "var(--terracotta)", label: "Series", Icon: Layers },
  newsletter: { color: "var(--gold)", label: "Newsletter", Icon: Mail },
};

function useDebounced(value, delay = 250) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

function TypeBadge({ type }) {
  const meta = TYPE_META[type] || {};
  const Icon = meta.Icon;
  return (
    <span
      className="inline-flex items-center gap-1.5 text-[0.6rem] uppercase tracking-[0.18em] px-2 py-1"
      style={{ background: "var(--bg-base)", color: meta.color, border: `1px solid ${meta.color}33` }}
      data-testid={`library-type-badge-${type}`}
    >
      {Icon && <Icon size={11} strokeWidth={2} />}
      {meta.label}
    </span>
  );
}

function LibraryCard({ item, expanded, onToggle }) {
  const meta = TYPE_META[item.type] || {};
  const navigate = useNavigate();
  const hasLink = !!item.link;

  const handleClick = () => {
    if (hasLink) {
      navigate(item.link);
    } else {
      onToggle(item.id);
    }
  };

  return (
    <article
      data-testid={`library-card-${item.type}-${item.id}`}
      className="card-surface p-6 cursor-pointer group transition-colors"
      onClick={handleClick}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <TypeBadge type={item.type} />
        <span className="text-[0.65rem] uppercase tracking-wider" style={{ color: "var(--muted)" }}>
          {item.meta}
        </span>
      </div>

      <h3
        className="font-serif-display text-xl lg:text-2xl leading-tight transition-colors"
        style={{ color: "var(--ink)" }}
      >
        <span className="group-hover:text-[var(--ember)] transition-colors">{item.title}</span>
      </h3>

      {item.scripture && (
        <p className="font-serif-display-italic text-sm mt-2" style={{ color: "var(--terracotta)" }}>
          {item.scripture}
        </p>
      )}

      {item.preview && (
        <p className="mt-3 text-sm leading-relaxed line-clamp-3" style={{ color: "var(--ink-2)" }}>
          {item.preview}
          {item.type === "devotional" && item.preview.length >= 180 ? "…" : ""}
        </p>
      )}

      {expanded && item.body && (
        <div
          className="mt-5 pt-5 border-t whitespace-pre-line text-sm leading-relaxed"
          style={{ borderColor: "var(--border-subtle)", color: "var(--ink-2)" }}
          onClick={(e) => e.stopPropagation()}
        >
          {item.body}
          {item.downloads && item.downloads.length > 0 && (
            <div className="mt-5 pt-4 border-t" style={{ borderColor: "var(--border-subtle)" }}>
              <div className="overline mb-3" style={{ fontSize: "0.65rem" }}>Downloads</div>
              <ul className="space-y-2">
                {item.downloads.map((d, idx) => (
                  <li key={idx}>
                    <a
                      href={d.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`library-download-${item.id}-${idx}`}
                      className="inline-flex items-center gap-2 text-sm underline"
                      style={{ color: "var(--ember)" }}
                    >
                      <Download size={14} /> {d.label || d.url}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <div className="mt-4 flex items-center gap-2 text-xs uppercase tracking-[0.18em]" style={{ color: meta.color }}>
        {hasLink ? (
          <>Open <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform" /></>
        ) : (
          <>{expanded ? "Hide" : "Read"} {item.type === "newsletter" ? "letter" : item.type}</>
        )}
      </div>
    </article>
  );
}

export default function Library() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const [searchInput, setSearchInput] = useState(searchParams.get("q") || "");
  const debouncedSearch = useDebounced(searchInput, 250);
  const activeType = searchParams.get("type") || "all";
  const activeSort = searchParams.get("sort") || "newest";

  // Keep URL in sync with debounced search
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (debouncedSearch) next.set("q", debouncedSearch);
    else next.delete("q");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const setParam = useCallback(
    (key, value) => {
      const next = new URLSearchParams(searchParams);
      if (value && value !== "all") next.set(key, value);
      else next.delete(key);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  // Fetch on filter / sort / search change
  useEffect(() => {
    if (!user) return;
    setLoading(true);
    api
      .get("/members/library", {
        params: { q: debouncedSearch, type: activeType, sort: activeSort },
      })
      .then((res) => {
        setItems(res.data.items || []);
        setError(null);
      })
      .catch((e) => setError(e?.response?.status === 403 ? "membership" : "unknown"))
      .finally(() => setLoading(false));
  }, [user, debouncedSearch, activeType, activeSort]);

  // Counts per type (computed from current filtered set if 'all', otherwise we'll show indicators only)
  const counts = useMemo(() => {
    const c = { all: items.length, reading: 0, study: 0, series: 0, newsletter: 0 };
    items.forEach((i) => { c[i.type] = (c[i.type] || 0) + 1; });
    return c;
  }, [items]);

  if (!user) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-32 text-center">
        <Lock size={40} strokeWidth={1.5} style={{ color: "var(--terracotta)", margin: "0 auto" }} />
        <h1 className="font-serif-display text-3xl mt-6" style={{ color: "var(--ink)" }}>Sign in to continue</h1>
        <Link to="/login" data-testid="library-login-redirect" className="btn-primary mt-6 inline-flex">Sign in</Link>
      </div>
    );
  }

  if (error === "membership" || (!loading && !user.membership_active && !user.is_admin)) {
    return (
      <div data-testid="library-paywall" className="max-w-2xl mx-auto px-6 py-32 text-center">
        <Lock size={40} strokeWidth={1.5} style={{ color: "var(--terracotta)", margin: "0 auto" }} />
        <div className="overline mt-6">Members only</div>
        <h1 className="font-serif-display text-4xl mt-3" style={{ color: "var(--ink)" }}>This library is for members.</h1>
        <Link to="/membership" data-testid="library-subscribe-link" className="btn-primary mt-8 inline-flex">Become a member</Link>
      </div>
    );
  }

  return (
    <div data-testid="library-page" className="max-w-7xl mx-auto px-6 lg:px-12 py-12 lg:py-16">
      {/* Header */}
      <header className="mb-10 lg:mb-12">
        <div className="overline mb-3">For members</div>
        <h1 className="font-serif-display text-4xl lg:text-6xl tracking-[-0.03em] leading-[1]" style={{ color: "var(--ink)" }}>
          The <span className="font-serif-display-italic" style={{ color: "var(--ember)" }}>library</span>
        </h1>
        <p className="mt-5 max-w-2xl text-base lg:text-lg leading-relaxed" style={{ color: "var(--ink-2)" }}>
          Everything members get access to — Read With Me, Bible studies, series, and monthly newsletters. Search by title, scripture, or topic.
        </p>
      </header>

      {/* Sticky filter / search bar */}
      <div
        className="sticky top-0 z-20 -mx-6 lg:-mx-12 px-6 lg:px-12 py-5 mb-10 backdrop-blur-md"
        style={{
          background: "color-mix(in srgb, var(--bg-base) 86%, transparent)",
          borderBottom: "1px solid var(--border-subtle)",
        }}
      >
        {/* Search */}
        <div className="relative mb-4">
          <Search
            size={16}
            className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none"
            style={{ color: "var(--muted)" }}
          />
          <input
            data-testid="library-search-input"
            type="text"
            placeholder="Search title, scripture, or keyword…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-full pl-11 pr-10 py-3 text-sm bg-transparent border outline-none focus:border-[var(--ember)] transition-colors"
            style={{ borderColor: "var(--border-subtle)", color: "var(--ink)" }}
          />
          {searchInput && (
            <button
              data-testid="library-search-clear"
              onClick={() => setSearchInput("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1"
              style={{ color: "var(--muted)" }}
              aria-label="Clear search"
            >
              <X size={14} />
            </button>
          )}
        </div>

        {/* Type pills + Sort */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            {TYPES.map((t) => {
              const isActive = activeType === t.key;
              return (
                <button
                  key={t.key}
                  data-testid={`library-type-${t.key}`}
                  onClick={() => setParam("type", t.key)}
                  className="text-xs uppercase tracking-[0.18em] px-3.5 py-2 transition-colors"
                  style={{
                    background: isActive ? "var(--ember)" : "transparent",
                    color: isActive ? "var(--bg-base)" : "var(--ink-2)",
                    border: `1px solid ${isActive ? "var(--ember)" : "var(--border-subtle)"}`,
                    fontWeight: isActive ? 700 : 500,
                  }}
                >
                  {t.label}
                </button>
              );
            })}
          </div>

          <div className="relative inline-flex items-center gap-2">
            <span className="text-[0.65rem] uppercase tracking-[0.18em]" style={{ color: "var(--muted)" }}>Sort</span>
            <select
              data-testid="library-sort-select"
              value={activeSort}
              onChange={(e) => setParam("sort", e.target.value)}
              className="appearance-none text-xs uppercase tracking-[0.18em] pl-3 pr-8 py-2 bg-transparent border outline-none cursor-pointer"
              style={{ borderColor: "var(--border-subtle)", color: "var(--ink)" }}
            >
              {SORTS.map((s) => (
                <option key={s.key} value={s.key} style={{ background: "var(--bg-base)", color: "var(--ink)" }}>
                  {s.label}
                </option>
              ))}
            </select>
            <ChevronDown size={14} className="absolute right-2 pointer-events-none" style={{ color: "var(--muted)" }} />
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="flex items-center justify-between mb-6">
        <p className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--muted)" }}>
          {loading ? "Loading…" : `${counts.all} ${counts.all === 1 ? "item" : "items"}`}
        </p>
      </div>

      {!loading && items.length === 0 ? (
        <div className="card-surface p-16 text-center" data-testid="library-empty">
          <Search size={32} strokeWidth={1.5} style={{ color: "var(--muted)", margin: "0 auto" }} />
          <h3 className="font-serif-display text-2xl mt-5" style={{ color: "var(--ink)" }}>
            Nothing matches that yet.
          </h3>
          <p className="mt-3 text-sm" style={{ color: "var(--ink-2)" }}>
            Try a different scripture, topic, or clear your filters.
          </p>
          {(searchInput || activeType !== "all") && (
            <button
              data-testid="library-reset-filters"
              onClick={() => { setSearchInput(""); setSearchParams({}, { replace: true }); }}
              className="mt-6 btn-ghost"
            >
              Reset filters
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((it) => (
            <LibraryCard
              key={`${it.type}-${it.id}`}
              item={it}
              expanded={expandedId === `${it.type}-${it.id}`}
              onToggle={() =>
                setExpandedId(expandedId === `${it.type}-${it.id}` ? null : `${it.type}-${it.id}`)
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}
